import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../store/hooks';
import { ZONE_IDS } from '../store/slices/zonesSlice';
import { useZoneStatus, useMultipleZones } from '../hooks/useZoneStatus';
import { getZoneDisplayName } from '../utils/zoneUtils';
import { discoverAvailableZones } from '../utils/zoneTestUtils';
import { runAllApiTests } from '../utils/apiTestUtils';
import ZoneStatus from '../components/ZoneStatus';
import ZoneAdmin from '../components/ZoneAdmin';
import TcButton from '../components/TcButton';
import TcPaper from '../components/TcPaper';

export default function ZonesDemo() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [selectedZone, setSelectedZone] = useState<string>(ZONE_IDS.ORANG_LAUT);
    const [discoveryResults, setDiscoveryResults] = useState<any>(null);
    const [isDiscovering, setIsDiscovering] = useState(false);
    const [isTestingApi, setIsTestingApi] = useState(false);

    // Test with confirmed zones only
    const confirmedZoneIds = Object.values(ZONE_IDS);
    const { zonesData, isAnyLoading, hasAnyError, fetchAllZones } = useMultipleZones(confirmedZoneIds);

    // Test with specific zone
    const {
        zoneStatus,
        isLoading,
        error,
        isAvailable,
        isProcessing,
        fetchZoneStatus,
        claimZone,
        updateStatus,
        updatePayload,
    } = useZoneStatus(selectedZone);

    const handleDiscoverZones = async () => {
        setIsDiscovering(true);
        try {
            const results = await discoverAvailableZones();
            setDiscoveryResults(results);
        } catch (error) {
            console.error('Failed to discover zones:', error);
        } finally {
            setIsDiscovering(false);
        }
    };

    const handleTestApi = async () => {
        setIsTestingApi(true);
        try {
            await runAllApiTests();
            console.log('API tests completed - check console for results');
        } catch (error) {
            console.error('API test failed:', error);
        } finally {
            setIsTestingApi(false);
        }
    };

    const handleTestUpdateStatus = async () => {
        try {
            await updateStatus({
                status: 'available',
                claimedBy: '', // Reset claimedBy to empty string
                payload: {
                    option: 0,
                },
            });
            console.log('✅ Zone status updated to available');
        } catch (error) {
            console.error('❌ Failed to update zone status:', error);
        }
    };

    const handleTestUpdatePayload = async () => {
        try {
            await updatePayload({
                payload: {
                    option: 1, // Test with option 1 (first button selected)
                },
            });
            console.log('✅ Zone payload updated with option: 1');
        } catch (error) {
            console.error('❌ Failed to update zone payload:', error);
        }
    };

    return (
        <div className="main-content-wrapper bg-black !pt-[112px] unselectable">
            <TcPaper classNameStyle="my-2 !px-4">
                <h1 className='mb-4'>Zones Status API Demo</h1>
                <p>Testing ZonesStatus API integration - Currently focused on confirmed zones only</p>
                
                {/* API Authentication Test Section */}
                <div className="mt-4 p-4 bg-green-900/30 border border-green-500 rounded-lg">
                    <div className="flex flex-col gap-4 items-center justify-between mb-2">
                        <h3 className="text-lg font-semibold mx-auto">API Authentication Test</h3>
                        <TcButton
                            handleClick={handleTestApi}
                            disabled={isTestingApi}
                            classNameStyle="text-sm py-2 px-3"
                            hollow={true}
                        >
                            {isTestingApi ? 'Testing...' : 'Test API Authentication'}
                        </TcButton>
                        <p className="text-sm text-gray-300">
                            Test authentication headers and credential strategies for different endpoints
                        </p>
                    </div>
                </div>
                
                {/* Zone Discovery Section */}
                <div className="mt-4 p-4 bg-blue-900/30 border border-blue-500 rounded-lg">
                    <div className="flex flex-col gap-4 items-center justify-between mb-2">
                        <h3 className="text-lg font-semibold mx-auto">Zone Discovery</h3>
                        <TcButton
                            handleClick={handleDiscoverZones}
                            disabled={isDiscovering}
                            classNameStyle="text-sm py-2 px-3"
                            hollow={true}
                        >
                            {isDiscovering ? 'Discovering...' : 'Discover Available Zones'}
                        </TcButton>
                        <p className="text-sm text-gray-300">
                            Test which zone endpoints are actually implemented on the server
                        </p>
                    </div>

                    {discoveryResults && (
                        <div className="mt-3 text-sm">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <span className="text-green-300 font-medium">✅ Available Zones:</span>
                                    <div className="text-green-200 ml-4">
                                        {discoveryResults.availableZones.length > 0 
                                            ? discoveryResults.availableZones.join(', ') 
                                            : 'None found'}
                                    </div>
                                </div>
                                <div>
                                    <span className="text-red-300 font-medium">❌ Unavailable Zones:</span>
                                    <div className="text-red-200 ml-4">
                                        {discoveryResults.unavailableZones.length > 0 
                                            ? discoveryResults.unavailableZones.join(', ') 
                                            : 'None'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </TcPaper>

            {/* Zone Selection - Only confirmed zones */}
            <TcPaper classNameStyle="my-4 !px-4">
                <h2 className="text-lg font-semibold mb-4">Select Confirmed Zone to Test</h2>
                <div className="grid grid-cols-2 gap-2 mb-4">
                    {confirmedZoneIds.map((zoneId) => (
                        <TcButton
                            key={zoneId}
                            handleClick={() => setSelectedZone(zoneId)}
                            primary={selectedZone === zoneId}
                            classNameStyle="text-sm py-2 px-3"
                        >
                            Zone {zoneId}
                        </TcButton>
                    ))}
                </div>
                <div className="text-sm text-gray-400">
                    Currently only testing confirmed zones. Use discovery to find more.
                </div>
            </TcPaper>

            {/* Individual Zone Status */}
            <TcPaper classNameStyle="my-4 !px-4">
                <h2 className="text-lg font-semibold mb-4">Zone {selectedZone} Status</h2>
                <ZoneStatus 
                    zoneId={selectedZone}
                    zoneName={`Test Zone ${selectedZone}`}
                    showActions={true}
                    className="mb-4"
                    onClaimed={() => {
                        console.log(`✅ Zone ${selectedZone} claimed successfully`);
                    }}
                />

                {/* Test Actions */}
                <div className="flex flex-wrap gap-2 mt-4">
                    <TcButton
                        handleClick={fetchZoneStatus}
                        disabled={isLoading}
                        classNameStyle="text-sm py-2 px-3"
                        hollow={true}
                    >
                        Refresh Status
                    </TcButton>
                    
                    <TcButton
                        handleClick={handleTestUpdateStatus}
                        disabled={isLoading}
                        classNameStyle="text-sm py-2 px-3"
                        hollow={true}
                    >
                        Reset to Available
                    </TcButton>
                    
                    <TcButton
                        handleClick={handleTestUpdatePayload}
                        disabled={isLoading || !isProcessing}
                        classNameStyle="text-sm py-2 px-3"
                        hollow={true}
                    >
                        Update Payload
                    </TcButton>
                </div>

                {/* Status Information */}
                {zoneStatus && (
                    <div className="mt-4 p-4 bg-gray-800 rounded-lg">
                        <h3 className="text-md font-medium mb-2">Current Status Details:</h3>
                        <div className="text-sm space-y-1">
                            <div>Status: <span className="text-blue-300">{zoneStatus.status}</span></div>
                            <div>Available: <span className={isAvailable ? 'text-green-300' : 'text-red-300'}>{isAvailable ? 'Yes' : 'No'}</span></div>
                            <div>Processing: <span className={isProcessing ? 'text-yellow-300' : 'text-gray-300'}>{isProcessing ? 'Yes' : 'No'}</span></div>
                            {zoneStatus.claimedBy && <div>Claimed By: <span className="text-orange-300">{zoneStatus.claimedBy}</span></div>}
                        </div>
                    </div>
                )}
            </TcPaper>

            {/* All Zones Overview */}
            <TcPaper classNameStyle="my-4 !px-4">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold leading-tight">All Zones Overview</h2>
                    <TcButton
                        handleClick={fetchAllZones}
                        disabled={isAnyLoading}
                        classNameStyle="text-sm py-2 px-3"
                        hollow={true}
                    >
                        Refresh All
                    </TcButton>
                </div>
                
                {isAnyLoading && (
                    <div className="text-center py-4 text-yellow-300">
                        Loading zones...
                    </div>
                )}
                
                <div className="grid gap-3">
                    {zonesData.map(({ zoneId, status, isLoading: zoneLoading, error: zoneError }) => (
                        <div key={zoneId} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                            <div className="flex items-center gap-3">
                                <span className="font-medium">{`Zone ${zoneId}`}</span>
                                {zoneLoading && <span className="text-yellow-300 text-sm">Loading...</span>}
                                {zoneError && <span className="text-red-300 text-sm">Error: {zoneError}</span>}
                                {status && (
                                    <span className={`text-sm px-2 py-1 rounded ${
                                        status.status === 'available' ? 'bg-green-600' :
                                        status.status === 'processing' ? 'bg-blue-600' :
                                        'bg-red-600'
                                    }`}>
                                        {status.status}
                                    </span>
                                )}
                            </div>
                            <div className="text-sm text-gray-400">
                                {status ? new Date(status.updatedAt).toLocaleTimeString() : 'No data'}
                            </div>
                        </div>
                    ))}
                </div>
                
                {hasAnyError && (
                    <div className="mt-4 p-3 bg-red-900/50 border border-red-500 rounded-lg">
                        <span className="text-red-300">Some zones failed to load. Check individual zone errors above.</span>
                    </div>
                )}
            </TcPaper>

            {/* API Information */}
            <TcPaper classNameStyle="my-4 !px-4">
                <h2 className="text-lg font-semibold mb-4">API Integration Info</h2>
                <div className="text-sm space-y-2 text-gray-300">
                    <div>• <strong>Zone 4.1:</strong> Orang Laut exhibit zone</div>
                    <div>• <strong>API Endpoints:</strong> GET/PUT /zones-status/&#123;zone_id&#125;, POST /zones-status/&#123;zone_id&#125;/claim, PUT /zones-status/&#123;zone_id&#125;/payload</div>
                    <div>• <strong>Status Types:</strong> available, processing, error</div>
                    <div>• <strong>Auto-refresh:</strong> Zone status auto-fetches every 30 seconds</div>
                    <div>• <strong>Redux Integration:</strong> Full state management with loading/error handling</div>
                </div>
            </TcPaper>

            {/* Zone Admin Panel */}
            <TcPaper classNameStyle="my-4 !px-4">
                <h2 className="text-lg font-semibold mb-4">Zone Admin Panel</h2>
                <ZoneAdmin />
            </TcPaper>
        </div>
    );
} 